// src/store/authStore.ts
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface User {
  id: number
  nombre: string
  email: string
  rol: 'ADMINISTRADOR' | 'DISTRIBUIDOR' | 'CLIENTE'
}

// Token temporal en memoria para acceso inmediato (soluciona race condition)
let tempAccessToken: string | null = null
let tempRefreshToken: string | null = null
let sessionTimeoutId: number | null = null

export const getTempAccessToken = () => tempAccessToken
export const getTempRefreshToken = () => tempRefreshToken

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: User | null
  isHydrated: boolean
  setAuth: (accessToken: string, refreshToken: string, user: User) => void
  setAccessToken: (token: string) => void
  logout: () => void
  isAuthenticated: () => boolean
  setHydrated: (state: boolean) => void
  startSessionTimeout: () => void
  clearSessionTimeout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isHydrated: false,

      setAuth: (accessToken, refreshToken, user) => {
        // 1. Guardar en memoria temporal PRIMERO (acceso instantáneo)
        tempAccessToken = accessToken
        tempRefreshToken = refreshToken
        
        // 2. Guardar en localStorage de forma síncrona
        try {
          const data = { state: { accessToken, refreshToken, user, isHydrated: true }, version: 0 }
          localStorage.setItem('biodacar-auth', JSON.stringify(data))
        } catch (e) {
          console.error('Error guardando en localStorage:', e)
        }
        
        // 3. Actualizar Zustand state (puede ser async)
        set({ accessToken, refreshToken, user })
        
        // 4. Iniciar timeout de sesión
        get().startSessionTimeout()
      },

      setAccessToken: (token) => {
        tempAccessToken = token
        set({ accessToken: token })
      },

      logout: () => {
        tempAccessToken = null
        tempRefreshToken = null
        get().clearSessionTimeout()
        localStorage.removeItem('biodacar-auth')
        set({ accessToken: null, refreshToken: null, user: null })
      },

      isAuthenticated: () => {
        const { accessToken } = get()
        return !!accessToken || !!tempAccessToken
      },

      setHydrated: (state) => {
        set({ isHydrated: state })
      },

      startSessionTimeout: () => {
        // Limpiar timeout anterior si existe
        if (sessionTimeoutId) {
          clearTimeout(sessionTimeoutId)
        }

        // Obtener duración de sesión desde localStorage (configuración)
        let sessionMinutes = 60 // Default: 60 minutos
        try {
          const configData = localStorage.getItem('biodacar-auth')
          if (configData) {
            const parsed = JSON.parse(configData)
            const sessionConfig = parsed?.state?.sessionDuration
            if (sessionConfig && !isNaN(parseInt(sessionConfig))) {
              sessionMinutes = parseInt(sessionConfig)
            }
          }
        } catch (e) {
          console.error('Error leyendo duración de sesión:', e)
        }

        // Configurar timeout (cliente side, sin consumir invocaciones)
        const timeoutMs = sessionMinutes * 60 * 1000
        sessionTimeoutId = setTimeout(() => {
          console.log('Sesión expirada por inactividad')
          get().logout()
          // Redirigir a login (será manejado por react-router)
          window.location.href = '/login'
        }, timeoutMs)

        console.log(`Timeout de sesión configurado: ${sessionMinutes} minutos`)
      },

      clearSessionTimeout: () => {
        if (sessionTimeoutId) {
          clearTimeout(sessionTimeoutId)
          sessionTimeoutId = null
        }
      }
    }),
    {
      name: 'biodacar-auth',
      onRehydrateStorage: () => (state) => {
        // Cuando rehidrata, también actualizar tokens temporales
        if (state) {
          tempAccessToken = state.accessToken
          tempRefreshToken = state.refreshToken
          state.setHydrated(true)
          
          // Reiniciar timeout de sesión después de rehidratar
          if (state.accessToken) {
            state.startSessionTimeout()
          }
        }
      }
    }
  )
)

// Función helper para esperar a que el estado se persista
export const waitForPersist = (): Promise<void> => {
  return new Promise((resolve) => {
    // Ya guardamos síncronamente, solo un pequeño delay para el state de React
    setTimeout(resolve, 10)
  })
}

